import React, { FunctionComponent, KeyboardEventHandler, MouseEventHandler, useCallback } from "react";

import { getCustomization } from "@multiweb/customization";
import { CheckBoxField, Icon, Layout, Skeleton, SpacingSize } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import {
    bemClassNames,
    createFocusWithinObserver,
    DataRowElementSiblings,
    doNothing,
    EMPTY_ARRAY,
    EMPTY_OBJECT,
    Key,
} from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { Role } from "@multiweb/uiShared/Role";
import { DataRowProps } from "../DataRow";
import { DataRowVariant } from "./enums";
import { getTabIndexByMultiselectMode } from "./utils";

const { openReactConnectorDetail } = bridge;

export type DataRowComponentProps = DataRowProps & {
    /** Obsahuje klíč pro renderer, kterým se má vyrenderovat template pro řádek. */
    templateRendererKey?: string;
    /** Určuje, jestli jsou načteny data pro daný záznam. */
    isRecordLoaded: boolean;
    /** Příznak určující, zda je daná row označena. */
    isChecked?: boolean;
    /** Příznak určující, zda je daná row vybrána. */
    isSelected?: boolean;
    /** Určuje, zda DataRow může zobrazit detail záznamu. */
    hasDetailForm?: boolean;
    /** Určuje, zda je DataSource v režimu výběru více záznamů. */
    multiselectMode?: boolean;
    /** Určuje, zda je pro DataSource povolen výběr více záznamů. */
    allowMultiselect?: boolean;
    /** Určuje, zda je vypnutý multi-výběr pomocí klávesy shift. */
    disableShiftKey?: boolean;
    /** Určuje, zda je komponenta vizuálně aktivní a zda s ní lze pracovat. */
    interactive?: boolean;
    /** Umožňuje na DataRow si nastavit vlastní tabIndex, bez ohledu na multiSelect mód. */
    explicitTabIndex?: number;
    /** Upravuje výchozí chování vzhledu. */
    variant?: DataRowVariant;
    /** Akce pro odchycení kliknutí na záznam. */
    handleRecordClick: () => void;
    /** Akce pro přepnutí označení(isChecked) na vybraných(isSelected) záznamech v DataSource. */
    toggleSelectedRecordsCheck: () => void;
    /** Akce pro výběr záznamů daných identifikátory. */
    selectRecords: (selectedRows: ReadonlyArray<string>) => void;
    /** Akce pro výběr dalšího záznamu. */
    selectNextRecord: (clearOtherSelections: boolean) => void;
    /** Akce pro výběr předchozího záznamu. */
    selectPreviousRecord: (clearOtherSelections: boolean) => void;
    /** Akce pro výběr záznamu daného identifikátorem. */
    trySelectRecord: () => void;
};

const classNames = bemClassNames("data-row-component");

export const DataRowComponent: FunctionComponent<DataRowComponentProps> = ({
    dataSourceKey,
    recordId,
    getSibling,
    templateRendererKey,
    isRecordLoaded,
    isChecked,
    isSelected,
    hasDetailForm,
    multiselectMode = false,
    allowMultiselect = false,
    disableShiftKey = false,
    interactive = true,
    explicitTabIndex = undefined,
    variant = DataRowVariant.default,
    handleRecordClick,
    toggleSelectedRecordsCheck,
    selectRecords,
    selectNextRecord,
    selectPreviousRecord,
    trySelectRecord,
}) => {
    // TODO: přesun do sagy??? - onClickDataRowAction(dataSourceKey, recordId) - odstraneni zavislosti allowMultiselect, handleRecordClick, openReactConnectorDetail, ...
    const onClick: MouseEventHandler = useCallback(
        (event) => {
            if (!isRecordLoaded) {
                // Jestliže není záznam dosud načtený, neděláme nic.
                return;
            }
            if (multiselectMode || (allowMultiselect && (event.ctrlKey || event.metaKey || event.shiftKey))) {
                handleRecordClick();
            } else if (hasDetailForm && interactive) {
                openReactConnectorDetail(dataSourceKey, recordId);
            }
        },
        [multiselectMode, allowMultiselect, hasDetailForm, interactive, isRecordLoaded, handleRecordClick, dataSourceKey, recordId],
    );

    // TODO: Přesun do sagy??? - onKeyDownDataRowAction(dataSourceKey, recordId???, key, shiftKey). Odstraneni zavislosti selectNextRecord, selectPreviousRecord, toggleSelectedRecordsCheck, ...
    const onKeyDown: KeyboardEventHandler = useCallback(
        (event) => {
            if (multiselectMode && isRecordLoaded) {
                const { key, shiftKey } = event;
                switch (key) {
                    case Key.ArrowDown:
                    case Key.ArrowUp: {
                        event.preventDefault();

                        const siblingType = key === Key.ArrowDown ? DataRowElementSiblings.nextElementSibling : DataRowElementSiblings.previousElementSibling;

                        const newFocusElement = getSibling(
                            siblingType,
                            document.activeElement,
                        );

                        if (!newFocusElement) {
                            return true; // Pokud jsem na ResponsiveListu, tak nemůžu selectovat elementy, které nevidím, ale v DataSource jsou načteny.
                        }

                        const reduxSelectMethod = key === Key.ArrowDown ? selectNextRecord : selectPreviousRecord;

                        if (newFocusElement.className.includes(classNames())) {
                            disableShiftKey ? reduxSelectMethod(true) : reduxSelectMethod(!shiftKey);
                        } else {
                            selectRecords(EMPTY_ARRAY);
                        }

                        newFocusElement.focus(); // Focus musí být až za akcí, jinak se vyvolá trySelect dříve než se provede aktualizace redux stavu a navigace se pokazí.
                        return true;
                    }
                    case Key.Space: {
                        event.preventDefault();
                        toggleSelectedRecordsCheck();
                        return true;
                    }
                    case Key.Escape: {
                        selectRecords([recordId]);
                        return true;
                    }
                    case Key.Tab: {
                        selectRecords(EMPTY_ARRAY);
                        return true;
                    }
                }
                return false;
            }
            return false;
        },
        [isRecordLoaded, multiselectMode, getSibling, selectNextRecord, selectPreviousRecord, disableShiftKey, selectRecords, toggleSelectedRecordsCheck, recordId],
    );

    // TODO: presun do sagy??? onFocusDataRowAction(). Odstraneni zavislosti trySelectRecord.
    const onRowFocus = useCallback(() => {
        if (multiselectMode) {
            trySelectRecord();
        }
    }, [multiselectMode, trySelectRecord]);
    const { onFocus } = createFocusWithinObserver(onRowFocus, doNothing, onRowFocus);

    /** TabIndex záznamu. Nastaví se až po načtení záznamu (nechceme, aby šlo zaměřit nenačtený záznam). */
    const tabIndex = isRecordLoaded ? (explicitTabIndex ?? getTabIndexByMultiselectMode(multiselectMode)) : undefined;

    const Template = getCustomization().liteListFrame.templates.getRendererByKey(templateRendererKey).component;

    return (
        <div
            className={classNames({
                clickable: hasDetailForm || multiselectMode,
                multiselectable: multiselectMode,
                isSelected: multiselectMode && isSelected,
                disabled: !interactive || !isRecordLoaded,
                variant,
            })}
            role={Role.row}
            aria-selected={multiselectMode ? isChecked || false : undefined}
            {...(interactive ? { onClick, onKeyDown, onFocus, tabIndex } : EMPTY_OBJECT)}
        >
            <div className={classNames("inner-wrapper")}>
                {hasDetailForm && !multiselectMode && (
                    <div className={classNames("arrow-icon")}>
                        <Icon iconCode={IconCode.uiChevronRightSmall} disabled={!interactive || !isRecordLoaded} />
                    </div>
                )}
                <div className={classNames("content")}>
                    {multiselectMode && (
                        <Layout spacingSizeRight={SpacingSize.m}>
                            {isRecordLoaded ? (
                                <CheckBoxField
                                    value={isChecked}
                                    disabled={!interactive}
                                    qa={"multiselectCheckBox"}
                                />
                            ) : (
                                <Skeleton height={"var(--base-2)"} width={"var(--base-2)"} />
                            )}
                        </Layout>
                    )}
                    <Template {...{ dataSourceKey, recordId }} />
                </div>
            </div>
        </div>
    );
};
